<template lang="pug">
.send-message-detail-contents
  .send-message-title
    div 送信メッセージ
  .message
    .header
      .address.d-flex.flex-row
        span(:class="_addressCssClass") 宛先： {{ _receivers }}
        .btn.address-toggle-btn
          .toggle-address.chevron-down(v-show="!isAddressOpen" @click="onAddressBadge()")
          .toggle-address.chevron-up(v-show="isAddressOpen" @click="onAddressBadge()")
      .address.d-flex.flex-row
        button.btn.read-list-btn(@click="onSendTargetList") 宛先一覧
    .body.d-flex.flex-column
      .message-text
        SendMessagePreview(v-model="value.functionContents" :datetime="value.datetime"
          :show-sub-title="getShowSubTitle(value.senderType)" :replyLimit="value.functionContents.replyLimit")

      .files(v-if="value.functionContents.fileUrls && 0 < value.functionContents.fileUrls.length")
        span.title 添付ファイル（{{ value.functionContents.fileUrls.length }}）
        button.btn.preview-btn(@click="onPreview") プレビュー

      .confirm-users.d-flex.flex-row(v-if="value.functionContents.addReadButton && value.functionContents.addReadButton == true")
        .reads
          button.btn.read-list-btn(v-if="readCount == -1" type="button")
            span 既読一覧
            b-spinner.spinner
          button.btn.read-list-btn.disable(v-else-if="readCount == 0" type="button" disabled) 既読一覧（{{ _readCount }}）
          button.btn.read-list-btn(v-else type="button" @click="onRead") 既読一覧（{{ _readCount }}）
        .unreads
          button.btn.read-list-btn(v-if="unreadCount == -1" type="button")
            span 未読一覧
            b-spinner.spinner
          button.btn.read-list-btn.disable(v-else-if="unreadCount == 0" type="button" disabled) 未読一覧（{{ _unreadCount }}）
          button.btn.unread-list-btn(v-else type="button" @click="onUnread") 未読一覧（{{ _unreadCount }}）
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import SendMessagePreview from "@/pc/basics/SendMessagePreview.vue";
import { SenderType } from "@/commons/models/Resident";
import { SendMessageHistoryTableItem } from "@/pc/containers/sendMessages/SendMessageHistoryTable.vue";

/**
 * メッセージ送信詳細コンポーネント
 */
@Component({
  components: {
    SendMessagePreview
  }
})
export default class SendMessageDetailContent extends Vue {
  /** 表示対象のメッセージ */
  @Prop({
    required: true
  })
  public value!: SendMessageHistoryTableItem;

  /** 既読人数 */
  @Prop()
  public readCount!: number;

  /** 未読人数 */
  @Prop()
  public unreadCount!: number;

  /** 宛先CSSクラス */
  public isAddressOpen: boolean = false;

  get _receivers(): string {
    let address: string = "";

    for (const receiver of this.value.receiverDetails) {
      // 詳細タブの宛先は家族の場合は（部屋名・利用者名）をつけるように仕様変更になったのでここで判定する
      let receiverName: string = receiver.room && receiver.residentName ? `${receiver.name}（${receiver.room}・${receiver.residentName}）` : receiver.name;

      if (address == "") {
        address += receiverName;
      } else {
        address += "｜" + receiverName;
      }
    }

    return address;
  }

  get _addressCssClass(): string {
    if (this.isAddressOpen) {
      return "address-open";
    } else {
      return "address-close";
    }
  }

  get _readCount(): string {
    if (99 < this.readCount) {
      return "99人以上";
    } else {
      return `${this.readCount}人`;
    }
  }

  get _unreadCount(): string {
    if (99 < this.unreadCount) {
      return "99人以上";
    } else {
      return `${this.unreadCount}人`;
    }
  }

  public getShowSubTitle(senderType: SenderType): boolean {
    if (senderType == SenderType.LINE) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### 宛先のオープン/クローズ切り替え
   */
  public onAddressBadge(): void {
    this.isAddressOpen = !this.isAddressOpen;

    return;
  }

  /**
   * ### プレビューボタン押下を親コンポーネントに通知する
   */
  public onSendTargetList(): void {
    this.$emit("onSendTargetList");

    return;
  }

  /**
   * ### プレビューボタン押下を親コンポーネントに通知する
   */
  public onPreview(): void {
    this.$emit("onPreview");

    return;
  }

  /**
   * ### プレビューボタン押下を親コンポーネントに通知する
   */
  public onRead(): void {
    this.$emit("onRead");

    return;
  }

  /**
   * ### プレビューボタン押下を親コンポーネントに通知する
   */
  public onUnread(): void {
    this.$emit("onUnread");

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.send-message-detail-contents {
  border: solid 1px $border-darkgray;
  position: relative;
  height: calc(100vh - 245px);
  overflow-y: auto;
  overflow-x: auto;
  background-color: #ffffff;

  .send-message-title {
    background-color: $bg-header;
    padding: 5px;
    min-height: 37px;
    font-weight: bold;
    text-align: center;
    border-bottom: solid 2px $border-lightgray;
  }
  .message {
    .header {
      padding: 10px 20px 10px 20px;

      .address {
        .address-close,
        .address-open {
          width: calc(100% - 24px);
          font-size: 18px;
        }
        .address-close {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .address-toggle-btn {
          padding: 1px 2px !important;
          height: 26px !important;
          .toggle-address {
            width: 20px;
            font-size: 18px;
          }
          .chevron-down {
            @include svg-icon("../../../assets/chevron-down.svg", $font-base-color, 20px, 20px);
          }
          .chevron-up {
            @include svg-icon("../../../assets/chevron-up.svg", $font-base-color, 20px, 20px);
          }
        }
      }
    }

    .body {
      .message-text,
      .files,
      .reads {
        padding: 20px 20px 20px 20px;
      }
      .unreads {
        padding: 20px 20px 20px 0;
      }
      .message-text {
        background-color: $bg-message;
      }
      .spinner {
        margin: 0 10px 5px 10px;
        width: 18px;
        height: 18px;
        color: #555555;
      }
    }
  }
}
</style>
